/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean;
}

export interface ApiConfig {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
  onTokenExpiry?: () => void;
}

export class HttpClient {
  private instance: AxiosInstance;
  private refreshInstance: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;
  private config: ApiConfig;

  constructor(config: ApiConfig = {}) {
    this.config = config;
    this.instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.refreshInstance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.initializeInterceptors();
  }

  private initializeInterceptors() {
    this.instance.interceptors.response.use(
      (response) => {
        const payload: any = response.data as any;
        if (
          payload &&
          typeof payload === "object" &&
          "success" in payload &&
          "data" in payload
        ) {
          response.data = payload.data;
        }
        if (this.config.onSuccess) {
          this.config.onSuccess(response);
        }
        return response;
      },
      async (error: AxiosError) => {
        const config = error.config as (CustomAxiosRequestConfig & {
          _retry?: boolean;
        });

        if (
          error.code === "ECONNABORTED" ||
          error.message?.includes("timeout")
        ) {
          error.message = "Connection timed out. Please try again.";
        }

        if (error.response?.status === 401) {
          const requestUrl = String(config?.url || "");
          const isAuthEndpoint =
            requestUrl.includes("/auth/login") ||
            requestUrl.includes("/auth/logout") ||
            requestUrl.includes("/auth/refresh") ||
            requestUrl.includes("/auth/setup") ||
            requestUrl.includes("/auth/reset-password") ||
            requestUrl.includes("/auth/forgot-password");

          if (config?.skipAuthRedirect || isAuthEndpoint) {
            if (!config?.skipAuthRedirect && this.config.onTokenExpiry) {
              this.config.onTokenExpiry();
            }
            return Promise.reject(error);
          }

          if (config?._retry) {
            if (this.config.onTokenExpiry) this.config.onTokenExpiry();
            return Promise.reject(error);
          }

          config._retry = true;

          try {
            if (!this.refreshPromise) {
              this.refreshPromise = this.refreshInstance
                .post("/auth/refresh")
                .then(() => undefined)
                .finally(() => {
                  this.refreshPromise = null;
                });
            }
            await this.refreshPromise;
            return this.instance.request(config);
          } catch (refreshError: any) {
            if (this.config.onTokenExpiry) this.config.onTokenExpiry();
            return Promise.reject(refreshError);
          }
        } else if (this.config.onError) {
          this.config.onError(error);
        }
        return Promise.reject(error);
      },
    );
  }

  public async request<T = any>(
    config: CustomAxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.request<T>(config);
  }
}

const httpClient = new HttpClient({
  onTokenExpiry: () => {
    window.location.href = "/login";
  },
  onError: (error) => {
    logger.error("API Error:", error);
  },
});

export const api = {
  request: <T = any>(config: CustomAxiosRequestConfig) =>
    httpClient.request<T>(config),
};

export default httpClient;
